import { rootUiActions } from 'app-components/flux/root-ui';
import classNames from 'classnames';
import StdDialog from 'components/Dialogs/StdDialog';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
/* Standard */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/* Material UI */
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
// DATA
import { goalsActions, goalsSelectors, goalsTypes } from 'app-components/flux/goals';
import { EPOCH_ISO } from 'app-components/utils/date';

import LoadingView from 'components/LoadingView';
/* Shared Components & Business Logic */
import QAmountField from 'components/QAmountField';
import QButton from 'components/QButton';
import QDateSelect from 'components/QDateSelect';
import QSwitch from 'components/QSwitch';
import QTip from 'components/QuickenControls/QTip';

import { calcMonthlyDiff, fmtAmt } from '../utils';
import EditGoalSuggestions from './EditGoalSuggestions';
import useStyles from './styles';
import { useRawPrivacyModeMask } from 'hooks/useRawPrivacyModeMask';

const EditGoal = (props) => {
  /* Prop Types */
  EditGoal.propTypes = {
    goal: PropTypes.shape({
      // full goal object passed as prop
      name: PropTypes.string,
      id: PropTypes.string, // goal id
      type: PropTypes.string, // type of goal, i.e. SAVE_FOR_VACATION, etc.
      accountId: PropTypes.string, // id of account assoc'd with goal
      startOn: PropTypes.string,
      targetOn: PropTypes.string, // target date
      completedOn: PropTypes.string,
      targetAmount: PropTypes.number,
      contributionAmount: PropTypes.number, // monthly amount to contribute
      isTakenFromF2S: PropTypes.bool,
      savedSoFar: PropTypes.number,
    }),
    dialogId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // used to close the dialog
    context: PropTypes.string,
    callback: PropTypes.func,
  };

  /* Component Level Variables */
  const { dialogId, goal, context, callback } = props;
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const existingGoals = useSelector(goalsSelectors.getActiveGoalsById);
  const showSpendingPlan = useSelector((state) =>
    featureFlagsSelectors.getFeatureFlags(state).get('goalsSpendingPlan'),
  );

  /* Initial State Values */
  const initialTargetAmount = goal?.targetAmount
    ? { value: goal.targetAmount, valid: true }
    : { value: null, valid: false };
  const initialGoalName = goal?.name ? { value: goal.name, valid: true } : { value: null, valid: false };
  const contributionAmount = goal.contributionAmount || 0; //  Sometimes it can be 'undefined', this fixes those and sets them to 0
  const initialEditedContribution =
    contributionAmount >= 0 ? { value: contributionAmount, valid: true } : { value: null, valid: false };
  const initialWantTargetDate = goal.targetOn && goal.targetOn !== EPOCH_ISO;

  /* State */
  const [goalProcessing, setGoalProcessing] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false); // hide/show suggestion checkboxes
  const [idealDate, setIdealDate] = useState(); // what user should change date to based on current target & monthly
  const [idealAmount, setIdealAmount] = useState(); // ditto above but based on current date

  // Below are all variables assoc'd with the 'form entries'
  const [editedName, setEditedName] = useState(initialGoalName);
  const [editedTargetAmount, setEditedTargetAmount] = useState(initialTargetAmount);
  const [editedDate, setEditedDate] = useState(() => ({
    value:
      goal.targetOn && goal.targetOn !== EPOCH_ISO
        ? DateTime.fromISO(goal.targetOn)
        : DateTime.local().plus({ months: 12 }),
    valid: true,
  }));
  const [editedContribution, setEditedContribution] = useState(initialEditedContribution);
  const [wantTargetDate, setWantTargetDate] = useState(initialWantTargetDate); // controls the QSwitch on/off
  const [includeInSpend, setIncludeInSpend] = useState(Boolean(goal.isTakenFromF2S)); // controls the QSwitch on/off
  const maxAbsoluteValue = 999999999.99;

  const privacyModeMask = useRawPrivacyModeMask();

  /* Helper Functions */

  const closeDialog = () => dispatch(rootUiActions.removeDialog(dialogId));

  const handleCancel = () => {
    callback?.({ success: false, error: 'User cancelled' });
    closeDialog();
  };

  // This function handles clicking the 'save' button and updating the goal
  const upsertNewGoal = () => {
    const goalToUpsert = goalsTypes.mkUpdateGoal(goal.type, {
      id: goal.id,
      targetAmount: editedTargetAmount.value,
      name: editedName.value,
      targetOn: wantTargetDate ? editedDate.value.toISODate() : EPOCH_ISO, // must send epoch to unset this value
      contributionAmount: editedContribution.value,
      isTakenFromF2S: includeInSpend,
      completedOn: goal.savedSoFar >= editedTargetAmount.value ? DateTime.local().toISO() : EPOCH_ISO, // must send epoch to unset this value
    });
    setGoalProcessing(true);
    dispatch(goalsActions.updateGoal(goalToUpsert, { context }));
    setTimeout(() => closeDialog(), 1500);
  };

  // This function creates a date on the first day of current month and year
  // Useful for calculating idealized values or dates because cannot diff from today b/c
  // QCS starts on the 1st of any month and ends on the last day
  const createStartDate = () => {
    const { month, year } = DateTime.local();
    return DateTime.fromFormat(`${month} 1 ${year}`, 'M d yyyy');
  };

  // This function will assess validity of all fields and submit if valid
  const validateAndSubmit = () => {
    if (editedTargetAmount.valid && editedName.valid && editedDate.valid && editedContribution.valid) {
      upsertNewGoal();
      callback?.({ success: true, goalId: goal?.id });
    }
  };

  // This function will be called on field change to calculate new 'suggested' monthly contribution
  const getIdealValues = (monthly, total, date) =>
    calcMonthlyDiff({
      monthlyContribution: monthly,
      targetAmount: total,
      targetDate: date,
      savedSoFar: goal.savedSoFar,
      startDate: createStartDate(),
    });

  // This function will be called when the user edits the name of their goal
  const handleNameChange = (newName) => {
    const formatName = (name) => name.trim().toUpperCase(); // internal name format f'n
    const currentName = formatName(goal.name);
    let validFlag = true;
    if (!newName) {
      // if empty string
      validFlag = false;
    } else if (existingGoals.size > 0) {
      // set invalid if there exists at least one match for the name
      validFlag = !existingGoals
        .valueSeq()
        .map((existingGoal) => formatName(existingGoal.name))
        .some((name) => name === formatName(newName) && name !== currentName);
    }
    setEditedName({ value: newName, valid: validFlag });
  };

  // This function will update 'ideal values' based on amount changes
  const updateIdeals = (target, monthly) => {
    const dateInUse = editedDate.value;
    const { idealHitDate, idealValue } = getIdealValues(monthly, target, dateInUse);
    // if the ideal date isn't the same as t
    if (!(idealHitDate.hasSame(dateInUse, 'month') && idealHitDate.hasSame(dateInUse, 'year'))) {
      setIdealAmount(idealValue); // setting state for display in EditSuggestions
      setIdealDate(idealHitDate);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // This function will be called when the user edits the contribution amount
  const handleContributionChange = (amount) => {
    updateIdeals(Number(editedTargetAmount.value), amount);
    if (amount < 0 || amount === null || amount === undefined || amount > editedTargetAmount.value) {
      setEditedContribution({ ...editedContribution, valid: false });
    } else {
      // a non-zero number < target amount
      setEditedContribution({ ...editedContribution, valid: true });
    }
  };

  // This function will be called when the user edits the target amount
  const handleTargetChange = (amount) => {
    updateIdeals(amount, Number(editedContribution.value));
    // eslint-disable-next-line babel/no-unused-expressions
    if (amount <= 0 || !amount || amount < editedContribution.value) {
      setEditedTargetAmount({ ...editedTargetAmount, valid: false }); // don't set the actual value b/c will unfocus field
    } else {
      // a non-zero number > monthly contribution
      setEditedTargetAmount({ ...editedTargetAmount, valid: true });
    }
  };

  // This function will handle blur of the contribution amount field
  const handleContributionBlur = (amount) => {
    let validFlag = true;
    if (amount === null || amount === undefined || amount < 0 || amount > editedTargetAmount.value) {
      validFlag = false;
    }
    setEditedContribution({ value: amount, valid: validFlag });
  };

  // This function will handle blur of the target amount field
  const handleTargetBlur = (amount) => {
    let validFlag = true;
    if (!amount || amount <= 0 || amount < editedContribution.value) {
      validFlag = false;
    }
    setEditedTargetAmount({ value: amount, valid: validFlag });
  };

  // This function handles upsert of date string and changing monthly contribution
  const upsertDate = (date) => {
    const { months } = date.diff(createStartDate(), 'months').toObject();
    if (months >= 1) {
      setEditedDate({ value: date, valid: true }); // if end date is after spoofed start date (i.e. 1st of current month), then it's valid
      const { idealHitDate, idealValue } = getIdealValues(editedContribution.value, editedTargetAmount.value, date);
      setIdealDate(idealHitDate);
      setEditedContribution({ value: idealValue, valid: true });
    } else {
      setEditedDate({ ...editedDate, valid: false }); // otherwise don't set field and mark it as invalid
    }
  };

  // This function creates a date string from month
  const handleEditMonth = (newMonth) => {
    const newDate = DateTime.fromFormat(`${newMonth} 28 ${editedDate.value.year}`, 'LLL d yyyy');
    upsertDate(newDate);
  };

  // This function creates date string from year
  const handleEditYear = (newYear) => {
    const newDate = DateTime.fromFormat(`${editedDate.value.month} 28 ${newYear}`, 'L d yyyy');
    upsertDate(newDate);
  };

  // This function will return how much user has left to save in their goal
  const getLeftToSave = () => {
    const left = Number(goal.targetAmount) - Number(goal.savedSoFar);
    return left < 0 ? 0 : left;
  };

  // This function handles the click of checkbox within EditGoalSuggestions
  const handleSuggestionDate = () => {
    setShowSuggestions(false);
    setEditedDate({ value: idealDate, valid: true });
    // don't leave value in error  state, set to amount left to save if in error
    if (!editedContribution.valid) setEditedContribution({ value: getLeftToSave(), valid: true });
  };

  // This function will render the header to the dialog or a loading skeleton
  const renderHeader = () => (
    <div className={classNames(classes.flexRow, classes.centerAlign)}>
      <QTip wrapOnly title={String(editedName.value)}>
        <Typography className={classes.headerOverflow} variant='titleMedium'>
          {editedName.value}
        </Typography>
      </QTip>
    </div>
  );

  // ===== Main Render =====
  return (
    <StdDialog
      classes={{ paperScrollPaper: classes.editDialog }}
      dialogId={dialogId}
      disableBackdropClick
      onClose={handleCancel}
      size='md'
      title={renderHeader()}
    >
      <DialogContent>
        {!goalProcessing ? (
          <div className={classes.flexRow}>
            {' '}
            {/* parent flex level (two main columns, L and R) */}
            <div className={classNames(classes.flexCol, classes.reducedWidth)}>
              {' '}
              {/* flex column on L side */}
              <div className={classes.rowPadding}>
                <div className={classes.flexCol}>
                  <Typography variant='bodyMedium' style={{ marginTop: 5 }}>
                    Saved so far
                  </Typography>
                  <Typography variant='bodyRegular'>{privacyModeMask(fmtAmt(goal.savedSoFar))}</Typography>
                </div>
              </div>
              <div className={classes.flexCol}>
                <TextField
                  label='Goal name'
                  value={String(editedName.value)}
                  error={!editedName.valid}
                  onChange={(e) => handleNameChange(e.target.value)}
                  id='edit_goal_name'
                  variant='filled'
                  helperText={editedName.valid ? ' ' : 'Choose a unique name'}
                  FormHelperTextProps={{
                    classes: {
                      contained: classNames(classes.helperText, classes.bottomMarginSm),
                    },
                  }}
                />
              </div>
              <div className={classes.flexCol}>
                <QAmountField
                  value={editedTargetAmount.value}
                  label='Target amount'
                  editable
                  decimals={2}
                  expectedSign='positive'
                  textFieldVariant='filled'
                  margin='normal'
                  onChange={(e) => handleTargetChange(Number(e.target.value))}
                  onBlur={(e) => handleTargetBlur(Number(e.target.value))}
                  maxVal={maxAbsoluteValue}
                  minVal={-maxAbsoluteValue}
                  inputId='edit_goal_amount'
                  textFieldProps={{
                    variant: 'outlined',
                    style: { minWidth: 'fit-content', maxWidth: 140 },
                    helperText: editedTargetAmount.valid ? ' ' : 'Choose a larger amount',
                    error: !editedTargetAmount.valid,
                    FormHelperTextProps: {
                      classes: {
                        contained: classes.helperText,
                      },
                    },
                  }}
                />
              </div>
            </div>
            <div className={classes.colDivider} />
            {/* grey divider bar between columns */}
            <div className={classes.flexCol} style={{ flexGrow: 1 }}>
              {' '}
              {/* flex column on R side */}
              <div className={classes.flexCol}>
                {' '}
                {/* flex row for switch button and text */}
                <div className={classes.flexRow}>
                  <Typography style={{ marginRight: 8 }} variant='bodyMedium'>
                    Target goal date{' '}
                  </Typography>
                  <QSwitch checked={wantTargetDate} onChange={() => setWantTargetDate(!wantTargetDate)} />
                </div>
                <QDateSelect
                  parentDivStyle={{
                    marginRight: 56,
                    marginBottom: 4,
                    opacity: wantTargetDate ? 1 : 0.5,
                    pointerEvents: wantTargetDate ? 'auto' : 'none',
                    transition: 'opacity 300ms ease-in',
                  }}
                  handleChangeMonth={(month) => handleEditMonth(month)}
                  handleChangeYear={(year) => handleEditYear(year)}
                  monthValue={editedDate.value?.monthShort}
                  monthLabel='Month'
                  yearValue={editedDate.value?.year}
                  yearLabel='Year'
                />
                <div style={{ marginBottom: !editedContribution.valid ? 8 : 0 }} className={classes.flexCol}>
                  <QAmountField
                    label='Monthly contribution'
                    decimals={2}
                    expectedSign='positive'
                    value={editedContribution.value}
                    editable
                    textFieldVariant='filled'
                    onChange={(e) => handleContributionChange(Number(e.target.value))}
                    onBlur={(e) => handleContributionBlur(Number(e.target.value))}
                    maxVal={maxAbsoluteValue}
                    minVal={-maxAbsoluteValue}
                    inputId='edit_monthly_contribution'
                    textFieldProps={{
                      variant: 'outlined',
                      style: { minWidth: 'fit-content', maxWidth: 140 },
                      helperText: !editedContribution.valid ? 'Invalid amount' : ' ',
                      error: !editedContribution.valid,
                      FormHelperTextProps: {
                        error: !editedContribution.valid,
                        classes: {
                          contained: classes.helperText,
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <EditGoalSuggestions
                dateInUse={editedDate.value}
                hasEndDate={wantTargetDate}
                amount={idealAmount}
                date={idealDate}
                changeAmount={() => {
                  setEditedContribution({ value: idealAmount, valid: true });
                  setShowSuggestions(false);
                }}
                changeDate={handleSuggestionDate}
                invalidContributionAmount={!editedContribution.valid}
                hide={!showSuggestions}
              />
              {showSpendingPlan && (
                <div className={classes.spendingPlanSelect}>
                  <Typography variant='paragraphRegular'>
                    {' '}
                    Subtract your planned contribution in your spending plan{' '}
                  </Typography>
                  <RadioGroup
                    value={includeInSpend}
                    onChange={() => setIncludeInSpend(!includeInSpend)}
                    className={classes.radioBtnContainer}
                  >
                    <FormControlLabel
                      control={<Radio id='subtract_radio_select' checked={includeInSpend} />}
                      label='Yes, subtract it'
                    />
                    <div className={classes.horizontalBorder} />
                    <FormControlLabel
                      control={<Radio id='ignore_radio_select' checked={!includeInSpend} />}
                      label='No, ignore it'
                    />
                  </RadioGroup>
                </div>
              )}
              <div className={classNames(classes.flexRow, classes.buttonPosition)}>
                {' '}
                {/* button/footer (save or cancel) */}
                <QButton
                  className={classes.addMargin}
                  onClick={handleCancel}
                  variant='outlined'
                  id='edit_cancel_button'
                >
                  Cancel
                </QButton>
                <QButton
                  className={classes.addMargin}
                  onClick={validateAndSubmit}
                  variant='contained'
                  id='edit_save_button'
                  disabled={!editedName.valid || !editedTargetAmount.valid || !editedDate.valid || !editedContribution}
                >
                  Save
                </QButton>
              </div>
            </div>
          </div>
        ) : (
          <div className={classes.dialogSize}>
            <LoadingView size={100} />
          </div>
        )}
      </DialogContent>
    </StdDialog>
  );
}; // end component

export default EditGoal;
